import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import { LocalFeedObject, RemoteFeedObject } from 'vcs-common';

import { VideoDisplayMode } from '../../../features/meet/types';
import { LogoVideoCallIcon } from '../../icons/logo-videocall';
import { LocalVideo } from '../local-video';
import { RemoteVideo } from '../remote-videos';
import { MOBILE_SOLO_OVERLAY_BOTTOM_CLASS } from '../mobile-solo-layout';
import { ArrowButton } from '../../../shared/ui';

export interface IVideoPanelProps {
  displayMode: VideoDisplayMode;
  isPsychologist: boolean;
  localVideo?: LocalFeedObject | null;
  remoteVideos: RemoteFeedObject[];
  isMyVideoMuted: boolean;
  clientName: string;
  localParticipantIdentity?: string;
  raisedHandParticipantIds?: Set<string>;
  /** Показывать порядковый номер на плитках участников (для групповой терапии у психолога — да, у клиента — нет). */
  showParticipantNumbers?: boolean;
  /** Групповая терапия: без одностороннего отступа слева (симметричные поля). */
  isGroupCall?: boolean;
  /** Мобильный портрет 1:1: видео на весь экран, управление поверх видео (только для displayMode 'default'). */
  mobileFullscreen?: boolean;
}

/**
 * Вычисляет оптимальную сетку (строки × столбцы) для N плиток.
 * Предпочитает равномерное заполнение и пейзажную ориентацию (cols >= rows).
 */
function computeGridLayout(n: number): { rows: number; cols: number } {
  if (n <= 1) return { rows: 1, cols: 1 };
  if (n === 2) return { rows: 1, cols: 2 };
  if (n === 3) return { rows: 2, cols: 2 };
  if (n === 4) return { rows: 2, cols: 2 };
  if (n <= 6) return { rows: 2, cols: 3 };
  if (n <= 8) return { rows: 2, cols: 4 };
  if (n === 9) return { rows: 3, cols: 3 };
  if (n <= 10) return { rows: 2, cols: 5 };
  if (n <= 12) return { rows: 3, cols: 4 };
  if (n <= 15) return { rows: 3, cols: 5 };
  if (n === 16) return { rows: 4, cols: 4 };
  if (n <= 18) return { rows: 3, cols: 6 };
  if (n <= 20) return { rows: 4, cols: 5 };
  if (n <= 24) return { rows: 4, cols: 6 };
  if (n <= 25) return { rows: 5, cols: 5 };
  if (n <= 30) return { rows: 5, cols: 6 };
  // Для больших значений — квадратно-пейзажный алгоритм
  const cols = Math.ceil(Math.sqrt(n * 1.4));
  return { rows: Math.ceil(n / cols), cols };
}

export const VideoPanel: React.FC<IVideoPanelProps> = ({
  displayMode,
  isPsychologist,
  localVideo,
  remoteVideos,
  isMyVideoMuted,
  clientName,
  localParticipantIdentity,
  raisedHandParticipantIds,
  showParticipantNumbers = true,
  isGroupCall = false,
  mobileFullscreen = false,
}) => {
  const [gridScreenSharePage, setGridScreenSharePage] = useState(0);
  const isSideBySide = displayMode === 'side-by-side';
  const isRightColumn = displayMode === 'right-column';
  const isGrid = displayMode === 'grid';
  const isGridScreenShare = displayMode === 'grid-screen-share';
  const getTileKey = (tile: { type: 'remote'; item: RemoteFeedObject } | { type: 'local' }) => {
    if (tile.type === 'local') return 'local';
    if (tile.item.isScreenShare) {
      return `${tile.item.identity ?? tile.item.display ?? 'screen'}-screenshare`;
    }
    return tile.item.identity ?? tile.item.display ?? 'remote';
  };

  const totalTilesGridScreenShare = isGridScreenShare ? remoteVideos.length + (localVideo ? 1 : 0) : 0;
  const gridScreenShareNeedsPagination = totalTilesGridScreenShare > 12;

  useEffect(() => {
    if (isGridScreenShare && !gridScreenShareNeedsPagination && gridScreenSharePage > 0) {
      setGridScreenSharePage(0);
    }
  }, [isGridScreenShare, gridScreenShareNeedsPagination, gridScreenSharePage]);

  // Режим для показа экрана / МАК в групповой терапии: всегда макс. 6 колонок в ряд, все видео одного размера (1/6 ширины), при 6 — на всю ширину
  if (isGridScreenShare) {
    const totalTiles = remoteVideos.length + (localVideo ? 1 : 0);
    const COLS = 6;
    type Tile = { type: 'remote'; item: RemoteFeedObject } | { type: 'local' };
    const all: Tile[] = [
      ...remoteVideos.map((item) => ({ type: 'remote' as const, item })),
      ...(localVideo ? [{ type: 'local' as const }] : []),
    ];

    const renderTile = (t: Tile, key: string, participantIndex: number) => {
      if (t.type === 'remote') {
        return (
          <RemoteVideo
            key={key}
            remoteFeedObject={t.item}
            displayMode="default"
            stayOpenInCompactModeSwitch={isPsychologist}
            clientName={t.item.display || clientName}
            hideFitToggle
            isHandRaised={Boolean(t.item.identity && raisedHandParticipantIds?.has(t.item.identity))}
            participantIndex={showParticipantNumbers ? participantIndex : undefined}
          />
        );
      }
      return localVideo ? (
        <div key={key} className="relative w-full h-full">
          <LocalVideo
            displayMode="right-column"
            isVideoMuted={isMyVideoMuted}
            showHideButton={false}
            attachVideoStream={localVideo.attachVideo}
            clear={localVideo.remove}
            isHandRaised={Boolean(localParticipantIdentity && raisedHandParticipantIds?.has(localParticipantIdentity))}
          />
        </div>
      ) : null;
    };

    // 1–6 участников: один ряд; при <6 центрируем сетку, при 6 — на всю ширину; соотношение плитки 220:120
    if (totalTiles <= COLS) {
      const gridCols = totalTiles;
      const centered = totalTiles < COLS;
      return (
        <div className="relative w-full h-full pointer-events-auto flex justify-center">
          <div
            className={classNames(
              'grid gap-4 rounded-chip md:rounded-card overflow-hidden bg-transparent',
              centered && 'mx-auto',
            )}
            style={{
              gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
              gridTemplateRows: 'auto',
              ...(centered ? { width: `${(gridCols / COLS) * 100}%`, maxWidth: '100%' } : { width: '100%' }),
            }}
          >
            {all.map((t, i) => (
              <div
                key={getTileKey(t)}
                className="w-full aspect-[220/120] min-h-0 rounded-chip md:rounded-control overflow-hidden bg-ink"
              >
                {renderTile(t, getTileKey(t), i + 1)}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // 7–12 участников: два ряда без пагинации
    const PER_PAGE = 12;
    const needsPagination = totalTiles > PER_PAGE;

    if (!needsPagination) {
      const topRow = all.slice(0, COLS);
      const bottomRow = all.slice(COLS, totalTiles);
      const bottomCount = bottomRow.length;
      const centerBottomRow = bottomCount > 0 && bottomCount < COLS;
      return (
        <div className="relative w-full h-full pointer-events-auto flex flex-col items-center gap-4">
          {/* Первый ряд: всегда 6 колонок на всю ширину */}
          <div
            className={classNames('w-full grid gap-4 rounded-chip md:rounded-card overflow-hidden bg-transparent')}
            style={{
              gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`,
              gridTemplateRows: 'auto',
            }}
          >
            {topRow.map((t, i) => (
              <div
                key={getTileKey(t)}
                className="w-full aspect-[220/120] min-h-0 rounded-chip md:rounded-control overflow-hidden bg-ink"
              >
                {renderTile(t, getTileKey(t), i + 1)}
              </div>
            ))}
          </div>
          {/* Второй ряд: при <6 участниках центрируем */}
          <div
            className={classNames('grid gap-4 rounded-chip md:rounded-card overflow-hidden bg-transparent', 'mx-auto')}
            style={{
              gridTemplateColumns: `repeat(${bottomCount}, minmax(0, 1fr))`,
              gridTemplateRows: 'auto',
              width: centerBottomRow ? `${(bottomCount / COLS) * 100}%` : '100%',
              maxWidth: '100%',
            }}
          >
            {bottomRow.map((t, i) => (
              <div
                key={getTileKey(t)}
                className="w-full aspect-[220/120] min-h-0 rounded-chip md:rounded-control overflow-hidden bg-ink"
              >
                {renderTile(t, getTileKey(t), COLS + i + 1)}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // 13+ участников: по 12 на «слайд», стрелки влево/вправо
    const totalPages = Math.ceil(totalTiles / PER_PAGE);
    const start = gridScreenSharePage * PER_PAGE;
    const pageSlice = all.slice(start, start + PER_PAGE);
    const pageTopRow = pageSlice.slice(0, COLS);
    const pageBottomRow = pageSlice.slice(COLS, PER_PAGE);
    const canGoLeft = gridScreenSharePage > 0;
    const canGoRight = gridScreenSharePage < totalPages - 1;

    return (
      <div className="relative w-full h-full pointer-events-auto">
        <div
          className={classNames(
            'relative w-full h-full grid gap-4 rounded-chip md:rounded-card overflow-hidden',
            'bg-transparent',
          )}
          style={{
            gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`,
            gridTemplateRows: 'auto auto',
          }}
        >
          {pageTopRow.map((t, i) => (
            <div
              key={getTileKey(t)}
              className="w-full aspect-[220/120] min-h-0 rounded-chip md:rounded-control overflow-hidden bg-ink"
            >
              {renderTile(t, getTileKey(t), start + i + 1)}
            </div>
          ))}
          {pageBottomRow.map((t, i) => (
            <div
              key={getTileKey(t)}
              className="w-full aspect-[220/120] min-h-0 rounded-chip md:rounded-control overflow-hidden bg-ink"
            >
              {renderTile(t, getTileKey(t), start + COLS + i + 1)}
            </div>
          ))}
        </div>
        <ArrowButton
          direction="prev"
          size="md"
          tone="solid"
          label="Предыдущие участники"
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10"
          disabled={!canGoLeft}
          onClick={() => setGridScreenSharePage((p) => Math.max(0, p - 1))}
        />
        <ArrowButton
          direction="next"
          size="md"
          tone="solid"
          label="Следующие участники"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10"
          disabled={!canGoRight}
          onClick={() => setGridScreenSharePage((p) => Math.min(totalPages - 1, p + 1))}
        />
      </div>
    );
  }

  if (isGrid) {
    const totalTiles = remoteVideos.length + (localVideo ? 1 : 0);
    type Tile = { type: 'remote'; item: RemoteFeedObject } | { type: 'local' };
    const all: Tile[] = [
      ...remoteVideos.map((item) => ({ type: 'remote' as const, item })),
      ...(localVideo ? [{ type: 'local' as const }] : []),
    ];

    const renderTile = (t: Tile, key: string, participantIndex: number) => {
      if (t.type === 'remote') {
        return (
          <RemoteVideo
            key={key}
            remoteFeedObject={t.item}
            displayMode="default"
            stayOpenInCompactModeSwitch={isPsychologist}
            clientName={t.item.display || clientName}
            hideFitToggle
            isHandRaised={Boolean(t.item.identity && raisedHandParticipantIds?.has(t.item.identity))}
            participantIndex={showParticipantNumbers ? participantIndex : undefined}
          />
        );
      }
      return localVideo ? (
        <div key={key} className="relative w-full h-full">
          <LocalVideo
            displayMode="right-column"
            isVideoMuted={isMyVideoMuted}
            showHideButton={false}
            attachVideoStream={localVideo.attachVideo}
            clear={localVideo.remove}
            isHandRaised={Boolean(localParticipantIdentity && raisedHandParticipantIds?.has(localParticipantIdentity))}
          />
        </div>
      ) : null;
    };

    const { rows, cols } = computeGridLayout(totalTiles);
    const incompleteCount = totalTiles % cols; // 0 если все ряды полные
    const tileClass = 'min-h-0 rounded-chip md:rounded-control overflow-hidden bg-ink';

    // incompleteCount === 0 → все ряды полные, обычная сетка
    // incompleteCount === 1 → единственная плитка выглядит лучше снизу (n=3: 2+1)
    // incompleteCount > 1  → неполный ряд сверху, полные ряды снизу (n=5: 2+3, n=7: 3+4, …)
    const incompleteOnTop = incompleteCount > 1;

    const incompleteTiles =
      incompleteCount > 0 ? (incompleteOnTop ? all.slice(0, incompleteCount) : all.slice(-incompleteCount)) : [];
    const mainTiles =
      incompleteCount > 0 ? (incompleteOnTop ? all.slice(incompleteCount) : all.slice(0, -incompleteCount)) : all;

    // Неполный ряд: ОДИН flex-контейнер на весь столбец сетки (gridColumn 1/-1),
    // плитки внутри той же ширины, что в полном ряду (1/cols доли).
    // НЕ per-tile gridColumn — иначе каждая плитка создаёт собственную строку сетки.
    const renderPartialRow = (tiles: Tile[], startIdx: number) => (
      <div className="flex w-full justify-center items-stretch gap-4" style={{ gridColumn: '1 / -1' }}>
        {tiles.map((t, i) => (
          <div
            key={getTileKey(t)}
            className={`${tileClass} min-h-0`}
            style={{ flex: `0 0 calc((100% - ${cols - 1} * 1rem) / ${cols})` }}
          >
            {renderTile(t, getTileKey(t), startIdx + i + 1)}
          </div>
        ))}
      </div>
    );

    return (
      <div
        className="relative w-full h-full pointer-events-auto grid gap-4 overflow-hidden"
        style={{
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        }}
      >
        {/* Неполный ряд сверху (incompleteCount > 1) */}
        {incompleteCount > 0 && incompleteOnTop && renderPartialRow(incompleteTiles, 0)}
        {/* Полные ряды */}
        {mainTiles.map((t, i) => (
          <div key={getTileKey(t)} className={tileClass}>
            {renderTile(t, getTileKey(t), (incompleteOnTop ? incompleteCount : 0) + i + 1)}
          </div>
        ))}
        {/* Неполный ряд снизу (incompleteCount === 1) */}
        {incompleteCount > 0 && !incompleteOnTop && renderPartialRow(incompleteTiles, mainTiles.length)}
      </div>
    );
  }

  const regularVideos = remoteVideos.filter((v) => !v.isScreenShare);
  const screenShares = remoteVideos.filter((v) => v.isScreenShare);
  const isMobileFullscreen = mobileFullscreen && displayMode === 'default';

  // Мобильный 1:1 с инструментом на весь экран: две мини-плитки в полосе над инструментом (ничего не перекрывают)
  if (displayMode === 'mini-strip') {
    const miniRemote = regularVideos[0];

    return (
      <div className="pointer-events-auto flex flex-row items-start justify-end gap-2">
        {miniRemote && (
          <RemoteVideo
            remoteFeedObject={miniRemote}
            displayMode="mini"
            clientName={miniRemote.display || clientName}
            hideFitToggle
            isHandRaised={Boolean(miniRemote.identity && raisedHandParticipantIds?.has(miniRemote.identity))}
          />
        )}
        {localVideo && (
          <LocalVideo
            displayMode="mini"
            isVideoMuted={isMyVideoMuted}
            showHideButton={false}
            attachVideoStream={localVideo.attachVideo}
            clear={localVideo.remove}
            isHandRaised={Boolean(localParticipantIdentity && raisedHandParticipantIds?.has(localParticipantIdentity))}
          />
        )}
      </div>
    );
  }

  if (isRightColumn) {
    // Для режима right-column всегда показываем оба видеопотока (localVideo и remoteVideos)
    // даже если один из них null, чтобы правая часть была одинаковой у клиента и психолога
    return (
      <div className={classNames('relative w-full h-full pointer-events-auto')}>
        <div className="flex flex-col gap-[12px] h-full bg-ink-subtle p-[16px] rounded-card">
          {/* Всегда показываем localVideo, даже если он null, чтобы правая часть была одинаковой */}
          <div className="w-full flex-1 min-h-0" style={{ flex: '1 1 0%' }}>
            {localVideo ? (
              <div className="w-full h-full">
                <LocalVideo
                  displayMode={displayMode}
                  isVideoMuted={isMyVideoMuted}
                  showHideButton={false}
                  attachVideoStream={localVideo.attachVideo}
                  clear={localVideo.remove}
                  isHandRaised={Boolean(
                    localParticipantIdentity && raisedHandParticipantIds?.has(localParticipantIdentity),
                  )}
                />
              </div>
            ) : (
              <div className="w-full h-full bg-ink rounded-control flex items-center justify-center relative">
                <div className="absolute top-[10px] left-[15px] w-auto bg-ink-muted/50 rounded-chip flex px-[10px] py-[5px]">
                  <div className="text-ui-xs text-white">Вы</div>
                </div>
              </div>
            )}
          </div>
          {screenShares.length > 0 ? (
            screenShares.map((x: RemoteFeedObject, i) => (
              <div key={x.identity ?? `screen-${i}`} className="w-full flex-1 min-h-0" style={{ flex: '1 1 0%' }}>
                <div className="w-full h-full">
                  <RemoteVideo
                    remoteFeedObject={x}
                    displayMode={displayMode}
                    stayOpenInCompactModeSwitch={isPsychologist}
                    clientName={x.display || clientName}
                    isHandRaised={Boolean(x.identity && raisedHandParticipantIds?.has(x.identity))}
                  />
                </div>
              </div>
            ))
          ) : regularVideos.length > 0 ? (
            regularVideos.map((x: RemoteFeedObject, i) => (
              <div key={x.identity ?? `remote-${i}`} className="w-full flex-1 min-h-0" style={{ flex: '1 1 0%' }}>
                <div className="w-full h-full">
                  <RemoteVideo
                    remoteFeedObject={x}
                    displayMode={displayMode}
                    stayOpenInCompactModeSwitch={isPsychologist}
                    clientName={x.display || clientName}
                    isHandRaised={Boolean(x.identity && raisedHandParticipantIds?.has(x.identity))}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="w-full flex-1 min-h-0" style={{ flex: '1 1 0%' }}>
              <div className="w-full h-full bg-ink rounded-control flex items-center justify-center relative">
                {clientName && (
                  <div className="absolute top-[10px] left-[15px] w-auto bg-ink-muted/50 rounded-chip flex px-[10px] py-[5px]">
                    <div className="text-ui-xs text-white">{clientName}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isSideBySide) {
    return (
      <div className={classNames('fixed left-1/2 -translate-x-1/2 w-[96vw] pointer-events-auto')}>
        <div className="flex flex-row flex-wrap items-stretch justify-center gap-3">
          {screenShares.length > 0 &&
            screenShares.map((x: RemoteFeedObject, i) => (
              <div key={x.identity ?? `screen-${i}`} className="flex-1 min-w-0">
                <RemoteVideo
                  remoteFeedObject={x}
                  displayMode={displayMode}
                  stayOpenInCompactModeSwitch={isPsychologist}
                  clientName={x.display || clientName}
                  isHandRaised={Boolean(x.identity && raisedHandParticipantIds?.has(x.identity))}
                />
              </div>
            ))}
          {regularVideos.length > 0 &&
            regularVideos.map((x: RemoteFeedObject, i) => (
              <div key={x.identity ?? `remote-${i}`} className="flex-1 min-w-0">
                <RemoteVideo
                  remoteFeedObject={x}
                  displayMode={displayMode}
                  stayOpenInCompactModeSwitch={isPsychologist}
                  clientName={x.display || clientName}
                  isHandRaised={Boolean(x.identity && raisedHandParticipantIds?.has(x.identity))}
                />
              </div>
            ))}

          {localVideo && (
            <div className="flex-1 min-w-0">
              <LocalVideo
                displayMode={displayMode}
                isVideoMuted={isMyVideoMuted}
                showHideButton={false}
                attachVideoStream={localVideo.attachVideo}
                clear={localVideo.remove}
                isHandRaised={Boolean(
                  localParticipantIdentity && raisedHandParticipantIds?.has(localParticipantIdentity),
                )}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames('flex flex-col relative', {
        'w-full h-full': displayMode !== 'compact',
        'ml-[0.25rem]':
          !isMobileFullscreen &&
          !isGroupCall &&
          displayMode !== 'compact' &&
          (isPsychologist || (!isPsychologist && displayMode === 'equal')),
      })}
    >
      {/* Видеоблок */}
      <div
        className={classNames(
          'overflow-hidden',
          isMobileFullscreen ? 'rounded-none' : 'rounded-chip md:rounded-card',
          { 'bg-surface': displayMode === 'equal' },
          { 'bg-ink-subtle': displayMode !== 'compact' && displayMode !== 'equal' },
          { 'w-full h-full': displayMode !== 'compact' },
          {
            'absolute top-14 right-[15px] flex flex-col gap-5 overflow-visible items-end': displayMode === 'compact',
          },
          { 'z-[10]': isPsychologist && displayMode === 'compact' },
        )}
      >
        {screenShares.length > 0 &&
          screenShares.map((x: RemoteFeedObject, i) => (
            <RemoteVideo
              remoteFeedObject={x}
              key={x.identity ?? `screen-${i}`}
              displayMode={displayMode}
              stayOpenInCompactModeSwitch={isPsychologist}
              clientName={x.display || clientName}
              isHandRaised={Boolean(x.identity && raisedHandParticipantIds?.has(x.identity))}
            />
          ))}
        {regularVideos.length > 0 &&
          regularVideos.map((x: RemoteFeedObject, i) => (
            <RemoteVideo
              remoteFeedObject={x}
              key={x.identity ?? `remote-${i}`}
              displayMode={displayMode}
              stayOpenInCompactModeSwitch={isPsychologist}
              clientName={x.display || clientName}
              isHandRaised={Boolean(x.identity && raisedHandParticipantIds?.has(x.identity))}
              mobileFullscreen={isMobileFullscreen}
            />
          ))}

        {/* Local */}
        {localVideo && (
          <LocalVideo
            displayMode={displayMode}
            isVideoMuted={isMyVideoMuted}
            showHideButton={displayMode === 'compact'}
            attachVideoStream={localVideo.attachVideo}
            clear={localVideo.remove}
            isHandRaised={Boolean(localParticipantIdentity && raisedHandParticipantIds?.has(localParticipantIdentity))}
            mobileFullscreen={isMobileFullscreen}
          />
        )}

        {/* Иконка */}
        {displayMode !== 'compact' && (
          <div
            className={classNames(
              'absolute right-[15px] w-[89px] h-[25px] lg:w-[172px] lg:h-[50px] z-10',
              isMobileFullscreen ? MOBILE_SOLO_OVERLAY_BOTTOM_CLASS : 'bottom-[15px]',
            )}
          >
            <LogoVideoCallIcon className="w-full h-full" />
          </div>
        )}
      </div>
    </div>
  );
};
